"use client";
import { useState, useCallback, useMemo } from "react";
import JSZip from "jszip";
import { Icons } from "../Icons";
import { useSearchExtract } from "./ExtractSearchContext";
import { VideoItem } from "../VideoItem";
import { DownloadState, Video } from "@/type/video";

// 主组件
export default function DouyinUserPage() {
  const { info } = useSearchExtract();
  // 状态
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [downloadStates, setDownloadStates] = useState<
    Map<string, DownloadState>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const PAGE_SIZE = 6;

  // 计算属性
  const loadedVideos = useMemo(() => {
    if (!info) return [];
    return info.slice(0, (currentPage + 1) * PAGE_SIZE);
  }, [currentPage, info]);

  const hasMore = loadedVideos.length < info?.length;
  const selectedCount = selectedVideos.size;
  const isAllSelected =
    loadedVideos.length > 0 &&
    loadedVideos.every((v: Video) => selectedVideos.has(v.videoId));

  // 切换选中
  const toggleSelect = useCallback((videoId: string) => {
    setSelectedVideos((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  }, []);

  // 全选/取消全选
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(loadedVideos.map((v: Video) => v.videoId)));
    }
  }, [isAllSelected, loadedVideos]);

  // 开始下载
  const startDownload = useCallback(
    async (videoId: string, videoUrl: string) => {
      if (downloadStates.get(videoId)?.isDownloading) return;

      setDownloadStates((prev) => {
        const next = new Map(prev);
        next.set(videoId, {
          progress: 0,
          isDownloading: true,
          isComplete: false,
        });
        return next;
      });

      try {
        const res = await fetch(videoUrl);
        const total = Number(res.headers.get("content-length")) || 0;

        const reader = res.body!.getReader();
        const chunks: ArrayBuffer[] = [];
        let received = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(
            value.buffer.slice(
              value.byteOffset,
              value.byteOffset + value.byteLength,
            ),
          );
          received += value.length;

          const progress = total ? Math.min((received / total) * 100, 100) : 0;

          setDownloadStates((prev) => {
            const next = new Map(prev);
            next.set(videoId, {
              progress,
              isDownloading: true,
              isComplete: false,
            });
            return next;
          });
        }

        const blob = new Blob(chunks, { type: "video/mp4" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `${videoId}.mp4`;
        a.click();

        URL.revokeObjectURL(url);

        setDownloadStates((prev) => {
          const next = new Map(prev);
          next.set(videoId, {
            progress: 100,
            isDownloading: false,
            isComplete: true,
          });
          return next;
        });
      } catch (e) {
        setDownloadStates((prev) => {
          const next = new Map(prev);
          next.delete(videoId);
          return next;
        });
      }
    },
    [downloadStates],
  );

  const startImageDownload = useCallback(
    async (videoId: string, imageUrls: string[]) => {
      if (!imageUrls.length) return;
      if (downloadStates.get(videoId)?.isDownloading) return;

      setDownloadStates((prev) => {
        const next = new Map(prev);
        next.set(videoId, {
          progress: 0,
          isDownloading: true,
          isComplete: false,
        });
        return next;
      });

      try {
        if (imageUrls.length === 1) {
          const res = await fetch(imageUrls[0]);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url;
          a.download = `${videoId}.jpg`;
          a.click();
          URL.revokeObjectURL(url);

          setDownloadStates((prev) => {
            const next = new Map(prev);
            next.set(videoId, {
              progress: 100,
              isDownloading: false,
              isComplete: true,
            });
            return next;
          });
          return;
        }

        const zip = new JSZip();
        const total = imageUrls.length;

        for (let index = 0; index < imageUrls.length; index += 1) {
          const imageUrl = imageUrls[index];
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const contentType = response.headers.get("content-type") || "";
          let ext = "jpg";

          if (contentType.startsWith("image/")) {
            ext = contentType.replace("image/", "").split(";")[0] || "jpg";
            if (ext === "jpeg") ext = "jpg";
          }

          zip.file(`image_${index + 1}.${ext}`, blob);

          const progress = Math.min(((index + 1) / total) * 100, 100);
          setDownloadStates((prev) => {
            const next = new Map(prev);
            next.set(videoId, {
              progress,
              isDownloading: true,
              isComplete: false,
            });
            return next;
          });
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipUrl = URL.createObjectURL(zipBlob);

        const link = document.createElement("a");
        link.href = zipUrl;
        link.download = `${videoId}.zip`;
        link.click();
        URL.revokeObjectURL(zipUrl);

        setDownloadStates((prev) => {
          const next = new Map(prev);
          next.set(videoId, {
            progress: 100,
            isDownloading: false,
            isComplete: true,
          });
          return next;
        });
      } catch (e) {
        setDownloadStates((prev) => {
          const next = new Map(prev);
          next.delete(videoId);
          return next;
        });
      }
    },
    [downloadStates],
  );

  // 批量下载
  const startBatchDownload = useCallback(() => {
    selectedVideos.forEach((videoId) => {
      const state = downloadStates.get(videoId);
      if (!state?.isDownloading && !state?.isComplete) {
        const currentVideo = info.find((v: Video) => v.videoId === videoId);

        if (!currentVideo) return;

        setTimeout(() => {
          if (currentVideo.resourceType === "P") {
            startImageDownload(videoId, currentVideo.image);
            return;
          }
          startDownload(videoId, currentVideo.video || "");
        }, Math.random() * 1000);
      }
    });
    setSelectedVideos(new Set());
  }, [selectedVideos, downloadStates, startDownload, startImageDownload, info]);

  // 加载更多
  const loadMore = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      setCurrentPage((prev) => prev + 1);
      setIsLoading(false);
    }, 600);
  }, []);

  // 获取下载状态
  const getDownloadState = useCallback(
    (videoId: string): DownloadState => {
      return (
        downloadStates.get(videoId) || {
          progress: 0,
          isDownloading: false,
          isComplete: false,
        }
      );
    },
    [downloadStates],
  );

  function formatNumber(num: number): string {
    if (num >= 100000000) {
      return (num / 100000000).toFixed(1).replace(/\.0$/, "") + "亿";
    }
    if (num >= 10000) {
      return (num / 10000).toFixed(1).replace(/\.0$/, "") + "万";
    }
    return num.toString();
  }

  return (
    info && (
      <div className={` mx-full  pb-5  `}>
        {/* 头部 */}
        <header className="mb-8">
          <div className="bg-card-bg rounded-2xl  p-6 md:p-8  flex-col md:flex-row  transition-all duration-500 hover:shadow-md flex items-center justify-between mb-6 flex-wrap gap-4 px-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                视频解析结果
              </h1>
              <p className="text-sm text-gray-500">
                共解析到{" "}
                <span className="font-medium text-gray-700">{info.length}</span>{" "}
                个视频
              </p>
            </div>

            {/* 批量操作 */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-100"
              >
                {isAllSelected ? "取消全选" : "全选"}
              </button>
              <button
                onClick={startBatchDownload}
                disabled={selectedCount === 0}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none hover:-translate-y-px hover:shadow-md bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white"
              >
                {Icons.download("w-4 h-4")}
                <span>下载选中 ({selectedCount})</span>
              </button>
            </div>
          </div>
        </header>

        {/* 视频列表 */}
        <main>
          <div className="space-y-3">
            {loadedVideos.map((video: Video, index: number) => (
              <VideoItem
                key={video.videoId}
                video={video}
                index={index}
                isSelected={selectedVideos.has(video.videoId)}
                downloadState={getDownloadState(video.videoId)}
                onSelect={() => toggleSelect(video.videoId)}
                onDownload={() =>
                  video.resourceType === "P"
                    ? startImageDownload(video.videoId, video.image)
                    : startDownload(video.videoId, video.video)
                }
              />
            ))}
          </div>

          {/* 加载更多 */}
          <div className="mt-8 text-center">
            {hasMore && !isLoading && (
              <button
                onClick={loadMore}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-all duration-200 hover:shadow-md"
              >
                {Icons.chevronDown("w-4 h-4")}
                <span>加载更多</span>
              </button>
            )}

            {isLoading && (
              <p className="text-sm text-gray-500 animate-pulse">正在加载...</p>
            )}

            {!hasMore && (
              <p className="text-sm text-gray-400">已加载全部视频</p>
            )}
          </div>
        </main>
      </div>
    )
  );
}
